function getChapterTitle(chapterIndex, chapter) {
  if (chapter.topic) {
    return chapter.topic;
  }
  return `Chapter ${chapterIndex + 1}`;
}
function reconcileCourse(courseFromJobType, sections) {
  const course = courseFromJobType ?? { chapters: [] };
  const availableSectionIds = new Set(sections.map((section) => section.id));
  const usedSectionIds = new Set(
    course.chapters.flatMap(
      (chapter) => chapter.lessons.map((lesson) => lesson.interviewSectionDefinitionId)
    )
  );
  const unusedSections = sections.filter(
    (section) => !usedSectionIds.has(section.id)
  );
  return {
    ...course,
    chapters: [
      // Remove lessons that no longer have a corresponding interview section definition.
      ...course.chapters.map((chapter) => ({
        ...chapter,
        lessons: chapter.lessons.filter(
          (lesson) => availableSectionIds.has(lesson.interviewSectionDefinitionId)
        )
      })),
      // Add a new chapter for any interview section definitions that don't have a corresponding lesson.
      ...unusedSections.length ? [
        {
          id: generateUnusedSectionsChapterId(course),
          topic: "Foundations",
          lessons: unusedSections.map((section) => ({
            interviewSectionDefinitionId: section.id
          }))
        }
      ] : []
    ]
  };
}
function generateUnusedSectionsChapterId(course) {
  let unusedSectionsChapterIndex = 0;
  while (course.chapters.some(
    (chapter) => chapter.id === computeUnusedSectionsChapterId(unusedSectionsChapterIndex)
  )) {
    unusedSectionsChapterIndex++;
  }
  return computeUnusedSectionsChapterId(unusedSectionsChapterIndex);
}
function computeUnusedSectionsChapterId(index) {
  return `client:unused-sections-${index}`;
}
function removeChapter(course, chapterId) {
  const chapter = course.chapters.find((chapter2) => chapter2.id === chapterId);
  const newCourse = {
    ...course,
    chapters: course.chapters.filter((chapter2) => chapter2.id !== chapterId)
  };
  return [newCourse, chapter];
}
function removeLesson(course, sectionId) {
  const lesson = course.chapters.flatMap((chapter) => chapter.lessons).find((lesson2) => lesson2.interviewSectionDefinitionId === sectionId);
  const newCourse = {
    ...course,
    chapters: course.chapters.map((chapter) => ({
      ...chapter,
      lessons: chapter.lessons.filter(
        (lesson2) => lesson2.interviewSectionDefinitionId !== sectionId
      )
    }))
  };
  return [newCourse, lesson];
}
function moveChapterBefore(course, chapterToMoveId, targetChapterId) {
  if (chapterToMoveId === targetChapterId) {
    return course;
  }
  const [courseWithoutChapter, chapterToMove] = removeChapter(
    course,
    chapterToMoveId
  );
  if (!chapterToMove) {
    return course;
  }
  const targetIndex = courseWithoutChapter.chapters.findIndex(
    (chapter) => chapter.id === targetChapterId
  );
  if (targetIndex === -1) {
    return course;
  }
  return {
    ...courseWithoutChapter,
    chapters: courseWithoutChapter.chapters.toSpliced(
      targetIndex,
      0,
      chapterToMove
    )
  };
}
function moveChapterAfter(course, chapterToMoveId, targetChapterId) {
  if (chapterToMoveId === targetChapterId) {
    return course;
  }
  const [courseWithoutChapter, chapterToMove] = removeChapter(
    course,
    chapterToMoveId
  );
  if (!chapterToMove) {
    return course;
  }
  const targetIndex = courseWithoutChapter.chapters.findIndex(
    (chapter) => chapter.id === targetChapterId
  );
  if (targetIndex === -1) {
    return course;
  }
  return {
    ...courseWithoutChapter,
    chapters: courseWithoutChapter.chapters.toSpliced(
      targetIndex + 1,
      0,
      chapterToMove
    )
  };
}
function moveLessonBefore(course, lessonToMoveId, targetLessonId) {
  if (lessonToMoveId === targetLessonId) {
    return course;
  }
  const [courseWithoutLesson, lessonToMove] = removeLesson(
    course,
    lessonToMoveId
  );
  if (!lessonToMove) {
    return course;
  }
  const targetChapter = courseWithoutLesson.chapters.find(
    (chapter) => chapter.lessons.some(
      (l) => l.interviewSectionDefinitionId === targetLessonId
    )
  );
  if (!targetChapter) {
    return course;
  }
  const targetIndex = targetChapter.lessons.findIndex(
    (l) => l.interviewSectionDefinitionId === targetLessonId
  );
  if (targetIndex === -1) {
    return course;
  }
  return {
    ...courseWithoutLesson,
    chapters: courseWithoutLesson.chapters.map(
      (chapter) => chapter.id === targetChapter.id ? {
        ...chapter,
        lessons: chapter.lessons.toSpliced(targetIndex, 0, lessonToMove)
      } : chapter
    )
  };
}
function moveLessonAfter(course, lessonToMoveId, targetLessonId) {
  if (lessonToMoveId === targetLessonId) {
    return course;
  }
  const [courseWithoutLesson, lessonToMove] = removeLesson(
    course,
    lessonToMoveId
  );
  if (!lessonToMove) {
    return course;
  }
  const targetChapter = courseWithoutLesson.chapters.find(
    (chapter) => chapter.lessons.some(
      (l) => l.interviewSectionDefinitionId === targetLessonId
    )
  );
  if (!targetChapter) {
    return course;
  }
  const targetIndex = targetChapter.lessons.findIndex(
    (l) => l.interviewSectionDefinitionId === targetLessonId
  );
  if (targetIndex === -1) {
    return course;
  }
  return {
    ...courseWithoutLesson,
    chapters: courseWithoutLesson.chapters.map(
      (chapter) => chapter.id === targetChapter.id ? {
        ...chapter,
        lessons: chapter.lessons.toSpliced(
          targetIndex + 1,
          0,
          lessonToMove
        )
      } : chapter
    )
  };
}
function moveLesson(course, lessonToMoveId, targetChapterId) {
  const [courseWithoutLesson, lessonToMove] = removeLesson(
    course,
    lessonToMoveId
  );
  if (!lessonToMove) {
    return course;
  }
  const targetChapter = courseWithoutLesson.chapters.find(
    (chapter) => chapter.id === targetChapterId
  );
  if (!targetChapter) {
    return course;
  }
  if (course.chapters.find((c) => c.id === targetChapterId)?.lessons.some((l) => l.interviewSectionDefinitionId === lessonToMoveId)) {
    return course;
  }
  return {
    ...courseWithoutLesson,
    chapters: courseWithoutLesson.chapters.map(
      (chapter) => chapter.id === targetChapterId ? {
        ...chapter,
        lessons: [...chapter.lessons, lessonToMove]
      } : chapter
    )
  };
}
function moveLessonToChapterEnd(course, lessonToMoveId, targetChapterId) {
  const [courseWithoutLesson, lessonToMove] = removeLesson(
    course,
    lessonToMoveId
  );
  if (!lessonToMove) {
    return course;
  }
  if (!courseWithoutLesson.chapters.some(
    (chapter) => chapter.id === targetChapterId
  )) {
    return course;
  }
  return {
    ...courseWithoutLesson,
    chapters: courseWithoutLesson.chapters.map(
      (chapter) => chapter.id === targetChapterId ? {
        ...chapter,
        lessons: [...chapter.lessons, lessonToMove]
      } : chapter
    )
  };
}
function buildChapterIndexMap(course) {
  return new Map(
    course.chapters.map((chapter, index) => [chapter.id, index])
  );
}
function buildLessonLocationMap(course) {
  return new Map(
    course.chapters.flatMap(
      (chapter) => chapter.lessons.map(
        (lesson, index) => [
          lesson.interviewSectionDefinitionId,
          {
            chapterId: chapter.id,
            index
          }
        ]
      )
    )
  );
}
function haveSameLessonOrder(a, b) {
  if (a.lessons.length !== b.lessons.length) {
    return false;
  }
  return a.lessons.every(
    (lesson, index) => lesson.interviewSectionDefinitionId === b.lessons[index]?.interviewSectionDefinitionId
  );
}
function computeLessonChangeStatuses(initial, current, options = {}) {
  const changeMap = /* @__PURE__ */ new Map();
  if (!initial || !current) {
    return changeMap;
  }
  const editedLessonIds = new Set(options.editedLessonIds ?? []);
  const initialLessonLocations = buildLessonLocationMap(initial);
  for (const chapter of current.chapters) {
    for (const [index, lesson] of chapter.lessons.entries()) {
      const initialLocation = initialLessonLocations.get(
        lesson.interviewSectionDefinitionId
      );
      if (!initialLocation) {
        changeMap.set(lesson.interviewSectionDefinitionId, "new");
        continue;
      }
      if (initialLocation.chapterId !== chapter.id || initialLocation.index !== index || editedLessonIds.has(lesson.interviewSectionDefinitionId)) {
        changeMap.set(lesson.interviewSectionDefinitionId, "edited");
      }
    }
  }
  return changeMap;
}
function computeChapterChangeStatuses(initial, current, options = {}) {
  const changeMap = /* @__PURE__ */ new Map();
  if (!initial || !current) {
    return changeMap;
  }
  const initialChapterMap = new Map(
    initial.chapters.map((chapter) => [chapter.id, chapter])
  );
  const initialChapterIndexes = buildChapterIndexMap(initial);
  const lessonStatuses = computeLessonChangeStatuses(initial, current, options);
  for (const [index, chapter] of current.chapters.entries()) {
    const initialChapter = initialChapterMap.get(chapter.id);
    if (!initialChapter) {
      changeMap.set(chapter.id, "new");
      continue;
    }
    const hasEditedLesson = chapter.lessons.some(
      (lesson) => lessonStatuses.has(lesson.interviewSectionDefinitionId)
    );
    if (initialChapterIndexes.get(chapter.id) !== index || initialChapter.topic !== chapter.topic || !haveSameLessonOrder(initialChapter, chapter) || hasEditedLesson) {
      changeMap.set(chapter.id, "edited");
    }
  }
  return changeMap;
}
export {
  computeChapterChangeStatuses,
  computeLessonChangeStatuses,
  getChapterTitle,
  moveChapterAfter,
  moveChapterBefore,
  moveLesson,
  moveLessonAfter,
  moveLessonBefore,
  moveLessonToChapterEnd,
  reconcileCourse,
  removeChapter,
  removeLesson
};
//# sourceMappingURL=course.js.map
